// FeeStructureCard — the printable "what does this student pay" summary:
// every mandatory head, every active optional service (e.g. Transport),
// the concession, and the monthly/yearly totals. Three selectable layouts
// (see TEMPLATES below); all three render the same data, just arranged
// differently, so switching the chip in FeesTab never refetches anything.
import { PrintHeader } from '../../ui/PrintSheet';
import { inr, MONTH_NAMES } from '../../ui/format';

export const TEMPLATES = ['classic', 'modern', 'compact'];

function useLines(mandatoryMonthly, mandatoryOneTime, optionalActive, fees) {
  const monthlyMandatory = mandatoryMonthly.reduce((s, h) => s + h.amount, 0);
  const monthlyOptional = optionalActive.reduce((s, h) => s + h.amount, 0);
  const grossMonthly = monthlyMandatory + monthlyOptional;
  const netMonthly = fees?.monthly_amount ?? grossMonthly;
  const oneTimeTotal = mandatoryOneTime.reduce((s, h) => s + h.amount, 0);
  const yearlyTotal = netMonthly * 12 + oneTimeTotal;
  return { monthlyMandatory, monthlyOptional, grossMonthly, netMonthly, oneTimeTotal, yearlyTotal };
}

function StudentInfo({ student, className = '' }) {
  return (
    <div className={`grid grid-cols-2 gap-x-8 gap-y-1 text-[13.5px] ${className}`}>
      <span>Student: <b>{student.name}</b></span>
      <span>Class: <b>{student.class_name || '—'}</b>{student.roll_number ? <> · Roll <b>{student.roll_number}</b></> : null}</span>
      <span>Admission No.: <b>{student.admission_no || '—'}</b></span>
      <span>Parent: <b>{student.father_name || student.guardian_name || '—'}</b></span>
    </div>
  );
}

function ConcessionLine({ fees }) {
  if (!fees?.concession_type) return null;
  const amt = fees.concession_type === 'percent' ? `${fees.concession_value}%` : inr(fees.concession_value);
  return (
    <p className="text-[12.5px] text-[#405058] mt-2">
      Concession: <b>{amt} off</b> every month{fees.concession_reason ? ` — ${fees.concession_reason}` : ''}
    </p>
  );
}

export default function FeeStructureCard({ template, schoolName, student, fees, mandatoryMonthly, mandatoryOneTime, optionalActive }) {
  const totals = useLines(mandatoryMonthly, mandatoryOneTime, optionalActive, fees);
  if (template === 'modern') return <Modern {...{ schoolName, student, fees, mandatoryMonthly, mandatoryOneTime, optionalActive, totals }} />;
  if (template === 'compact') return <Compact {...{ schoolName, student, fees, mandatoryMonthly, mandatoryOneTime, optionalActive, totals }} />;
  return <Classic {...{ schoolName, student, fees, mandatoryMonthly, mandatoryOneTime, optionalActive, totals }} />;
}

function Classic({ schoolName, student, fees, mandatoryMonthly, mandatoryOneTime, optionalActive, totals }) {
  return (
    <div>
      <PrintHeader schoolName={schoolName} docTitle="Fee Structure Card" />
      <StudentInfo student={student} />
      <table className="w-full text-sm border-collapse mt-4">
        <thead><tr><th className="text-left">Fee head</th><th className="text-left">Charged</th><th className="text-right">Amount</th></tr></thead>
        <tbody>
          {mandatoryMonthly.map(h => (
            <tr key={h.id}><td>{h.head_name}</td><td>Every month</td><td className="text-right">{inr(h.amount)}</td></tr>
          ))}
          {optionalActive.map(h => (
            <tr key={h.head_id}><td>{h.head_name}</td><td>Every month (optional)</td><td className="text-right">{inr(h.amount)}</td></tr>
          ))}
          {mandatoryOneTime.map(h => (
            <tr key={h.id}><td>{h.head_name}</td><td>Once, in {MONTH_NAMES[h.due_month - 1]}</td><td className="text-right">{inr(h.amount)}</td></tr>
          ))}
          {!mandatoryMonthly.length && !optionalActive.length && !mandatoryOneTime.length && (
            <tr><td colSpan={3} className="text-center text-[#405058] py-3">No fee heads configured for this student yet.</td></tr>
          )}
          <tr className="border-t-2 border-[#101b22]"><td className="font-bold" colSpan={2}>Monthly fee (after concession)</td><td className="text-right font-bold">{inr(totals.netMonthly)}</td></tr>
          {totals.oneTimeTotal > 0 && <tr><td className="font-bold" colSpan={2}>One-time charges this session</td><td className="text-right font-bold">{inr(totals.oneTimeTotal)}</td></tr>}
          <tr><td className="font-bold" colSpan={2}>Yearly total</td><td className="text-right font-bold">{inr(totals.yearlyTotal)}</td></tr>
        </tbody>
      </table>
      <ConcessionLine fees={fees} />
      <div className="flex justify-end mt-12">
        <div className="border-t border-[#101b22] pt-1 px-6 text-xs text-[#405058]">Authorised signature</div>
      </div>
    </div>
  );
}

function Modern({ schoolName, student, fees, mandatoryMonthly, mandatoryOneTime, optionalActive, totals }) {
  return (
    <div>
      <div className="bg-transit text-white rounded-m px-6 py-4 mb-5">
        <div className="font-display font-extrabold text-xl">{schoolName}</div>
        <div className="text-[13px] opacity-90 mt-0.5">Fee Structure Card — {student.name}{student.class_name ? ` · Class ${student.class_name}` : ''}</div>
      </div>
      <StudentInfo student={student} />
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="border border-line rounded-m p-4">
          <h4 className="font-display font-bold text-[13px] mb-2">Monthly charges</h4>
          {[...mandatoryMonthly, ...optionalActive.map(h => ({ ...h, id: `o${h.head_id}`, isOptional: true }))].map(h => (
            <div key={h.id} className="flex justify-between text-sm py-0.5">
              <span>{h.head_name}{h.isOptional ? ' (optional)' : ''}</span><span className="tabular-nums">{inr(h.amount)}</span>
            </div>
          ))}
          {!mandatoryMonthly.length && !optionalActive.length && <p className="text-sm text-[#405058]">None configured</p>}
        </div>
        <div className="border border-line rounded-m p-4">
          <h4 className="font-display font-bold text-[13px] mb-2">One-time charges (this session)</h4>
          {mandatoryOneTime.map(h => (
            <div key={h.id} className="flex justify-between text-sm py-0.5">
              <span>{h.head_name} ({MONTH_NAMES[h.due_month - 1]})</span><span className="tabular-nums">{inr(h.amount)}</span>
            </div>
          ))}
          {!mandatoryOneTime.length && <p className="text-sm text-[#405058]">None</p>}
        </div>
      </div>
      <ConcessionLine fees={fees} />
      <div className="flex justify-end gap-8 mt-4 bg-paper rounded-m px-5 py-3">
        <span className="text-sm">Monthly: <b className="text-base">{inr(totals.netMonthly)}</b></span>
        <span className="text-sm">Yearly: <b className="text-base">{inr(totals.yearlyTotal)}</b></span>
      </div>
      <div className="flex justify-end mt-10">
        <div className="border-t border-[#101b22] pt-1 px-6 text-xs text-[#405058]">Authorised signature</div>
      </div>
    </div>
  );
}

function Compact({ schoolName, student, fees, mandatoryMonthly, mandatoryOneTime, optionalActive, totals }) {
  const rows = [
    ...mandatoryMonthly.map(h => ({ key: h.id, label: h.head_name, amount: h.amount })),
    ...optionalActive.map(h => ({ key: `o${h.head_id}`, label: `${h.head_name} (optional)`, amount: h.amount })),
    ...mandatoryOneTime.map(h => ({ key: h.id, label: `${h.head_name} (${MONTH_NAMES[h.due_month - 1]})`, amount: h.amount })),
  ];
  return (
    <div className="max-w-[360px] mx-auto border border-[#101b22] rounded-m p-4" style={{ fontSize: '12.5px' }}>
      <div className="text-center border-b border-[#101b22] pb-2 mb-2">
        <div className="font-display font-extrabold text-[15px]">{schoolName}</div>
        <div className="text-[10.5px] uppercase tracking-wider text-[#405058]">Fee Structure Card</div>
      </div>
      <div className="mb-2">
        <div><b>{student.name}</b></div>
        <div className="text-[#405058]">{student.class_name || '—'}{student.roll_number ? ` · Roll ${student.roll_number}` : ''}</div>
      </div>
      {rows.map(r => (
        <div key={r.key} className="flex justify-between py-0.5 border-b border-dotted border-[#9aa8a6]">
          <span>{r.label}</span><span className="tabular-nums">{inr(r.amount)}</span>
        </div>
      ))}
      {!rows.length && <p className="text-[#405058] py-2">No fee heads configured yet.</p>}
      <div className="flex justify-between font-bold mt-2 pt-1 border-t-2 border-[#101b22]">
        <span>Monthly</span><span className="tabular-nums">{inr(totals.netMonthly)}</span>
      </div>
      <div className="flex justify-between font-bold">
        <span>Yearly</span><span className="tabular-nums">{inr(totals.yearlyTotal)}</span>
      </div>
      <ConcessionLine fees={fees} />
    </div>
  );
}
